import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  flowLeadStatsMergeFieldsQuery,
  flowPlaceholdersQuery,
} from "pages/FlowBuilderv2/flowBuilderQueries";
import AiPromptEditor, { AiPromptEditorHandle } from "./AiPromptEditor";
import { startPlaceholderDrag } from "./placeholderDrag";
import {
  PLACEHOLDER_FIELDS,
  PlaceholdersResponse,
  buildPlaceholderValues,
} from "./placeholderFields";
import { LeadStatsMergeField } from "types/LeadStatsMergeField";

interface MessageTextEditorProps {
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  channel?: "sms" | "slack";
}

// Plain-text message editor for SMS and Slack: the same editor + placeholder
// dropdown as the email content tab, without the email-only style and preview.
const MessageTextEditor: FC<MessageTextEditorProps> = ({
  label,
  value,
  onChange,
  placeholder,
  channel = "sms",
}) => {
  const queryClient = useQueryClient();

  const [placeholders, setPlaceholders] = useState<PlaceholdersResponse>({});
  const [leadStatsFields, setLeadStatsFields] = useState<LeadStatsMergeField[]>(
    []
  );
  const [smsWarning, setSmsWarning] = useState<string | null>(null);
  useEffect(() => {
    (async () => {
      try {
        const data = (await queryClient.ensureQueryData(
          flowPlaceholdersQuery()
        )) as PlaceholdersResponse;
        setPlaceholders(data || {});
      } catch {
        setPlaceholders({});
      }
    })();
  }, [queryClient]);
  useEffect(() => {
    (async () => {
      try {
        const data = await queryClient.ensureQueryData(
          flowLeadStatsMergeFieldsQuery(channel === "sms" ? "sms" : "internal")
        );
        setLeadStatsFields(data || []);
      } catch {
        setLeadStatsFields([]);
      }
    })();
  }, [queryClient, channel]);
  const placeholderValues = useMemo(
    () => buildPlaceholderValues(placeholders),
    [placeholders]
  );
  const leadStatsPlaceholderValues = useMemo(
    () =>
      Object.fromEntries(
        leadStatsFields
          .filter((field) => !field.disabled)
          .map((field) => [
            field.token,
            field.exampleValue || field.displayName || field.token,
          ])
      ),
    [leadStatsFields]
  );
  const editorPlaceholderValues = useMemo(
    () => ({ ...placeholderValues, ...leadStatsPlaceholderValues }),
    [placeholderValues, leadStatsPlaceholderValues]
  );
  const availablePlaceholders = useMemo(
    () => PLACEHOLDER_FIELDS.filter((f) => placeholderValues[f.tag]),
    [placeholderValues]
  );
  const groupedLeadStatsFields = useMemo(() => {
    return leadStatsFields.reduce<Record<string, LeadStatsMergeField[]>>(
      (acc, field) => {
        const group = field.group || "LeadStats";
        acc[group] = [...(acc[group] || []), field];
        return acc;
      },
      {}
    );
  }, [leadStatsFields]);

  const editorRef = useRef<AiPromptEditorHandle>(null);
  const [placeholderMenuOpen, setPlaceholderMenuOpen] = useState(false);
  const placeholderMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!placeholderMenuOpen) return;
    const onClick = (e: MouseEvent) => {
      if (
        placeholderMenuRef.current &&
        !placeholderMenuRef.current.contains(e.target as Node)
      )
        setPlaceholderMenuOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [placeholderMenuOpen]);

  return (
    <div className="px-5 pb-5 flex flex-col gap-[8px] flex-1 min-h-0">
      <div className="flex items-center justify-between gap-2">
        <div className="font-semibold">{label}</div>
        <div className="relative shrink-0" ref={placeholderMenuRef}>
          <button
            type="button"
            onClick={() => setPlaceholderMenuOpen((o) => !o)}
            className="flex items-center gap-1 px-2 py-1 rounded text-[12px] border border-themed-borderSecondary bg-themed-card text-themed-text hover:border-[#7C3AED] hover:text-[#7C3AED]"
          >
            Placeholders
            <span className="text-[16px] leading-none">▾</span>
          </button>
          {placeholderMenuOpen && (
            <div className="absolute right-0 mt-1 z-10 w-[260px] max-h-[260px] overflow-y-auto bg-themed-card border border-themed-borderSecondary rounded shadow-lg py-1">
              {availablePlaceholders.length === 0 ? (
                <div className="px-3 py-2 text-[12px] text-themed-textSecondary leading-[18px]">
                  You haven&apos;t set up any placeholder values yet. Add them
                  on{" "}
                  <span className="font-medium">
                    Settings &rarr; Placeholders
                  </span>{" "}
                  to use them here.
                </div>
              ) : (
                availablePlaceholders.map((p) => (
                  <button
                    key={p.tag}
                    type="button"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData("text/placeholder", p.tag);
                      e.dataTransfer.effectAllowed = "move";
                      startPlaceholderDrag(e, placeholderValues[p.tag] || "");
                      setTimeout(() => setPlaceholderMenuOpen(false), 0);
                    }}
                    onClick={() => {
                      editorRef.current?.insertPlaceholder(p.tag);
                      setPlaceholderMenuOpen(false);
                    }}
                    className="w-full text-left px-3 py-1.5 hover:bg-themed-background flex items-center gap-2 cursor-grab"
                  >
                    <span
                      className="inline-block align-baseline rounded px-1 text-[13px] truncate"
                      style={{
                        color: "var(--foreground)",
                        backgroundColor:
                          "color-mix(in srgb, var(--muted) 50%, transparent)",
                      }}
                    >
                      {placeholderValues[p.tag]}
                    </span>
                  </button>
                ))
              )}
              {Object.entries(groupedLeadStatsFields).map(([group, fields]) => (
                <div
                  key={group}
                  className="border-t border-themed-border mt-1 pt-1"
                >
                  <div className="px-3 py-1 text-[11px] uppercase tracking-wide text-themed-textSecondary">
                    {group}
                  </div>
                  {fields.map((field) => (
                    <button
                      key={field.id}
                      type="button"
                      disabled={field.disabled}
                      draggable={!field.disabled}
                      onDragStart={(e) => {
                        if (field.disabled) return;
                        e.dataTransfer.setData("text/placeholder", field.token);
                        e.dataTransfer.effectAllowed = "move";
                        startPlaceholderDrag(
                          e,
                          field.exampleValue || field.displayName
                        );
                        setTimeout(() => setPlaceholderMenuOpen(false), 0);
                      }}
                      onClick={() => {
                        if (field.disabled) return;
                        editorRef.current?.insertPlaceholder(field.token);
                        setSmsWarning(
                          channel === "sms"
                            ? field.smsLengthWarning ?? null
                            : null
                        );
                        setPlaceholderMenuOpen(false);
                      }}
                      className={`w-full text-left px-3 py-1.5 flex flex-col gap-0.5 ${
                        field.disabled
                          ? "opacity-50 cursor-not-allowed"
                          : "hover:bg-themed-background cursor-grab"
                      }`}
                    >
                      <span className="text-[13px] text-themed-text">
                        {field.displayName}
                      </span>
                      <span className="text-[11px] text-themed-textSecondary">
                        {`{{ ${field.token} }}`}
                        {field.disabled && field.disabledReason
                          ? ` - ${field.disabledReason}`
                          : ""}
                      </span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      {channel === "sms" && smsWarning && (
        <div className="text-[12px] leading-[18px] text-[#B45309]">
          {smsWarning}
        </div>
      )}
      <AiPromptEditor
        ref={editorRef}
        value={value}
        onChange={onChange}
        placeholderValues={editorPlaceholderValues}
        placeholder={placeholder}
        className="flex-1 min-h-0"
      />
    </div>
  );
};

export default MessageTextEditor;
